import React, { useMemo, useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { View, Image } from 'react-native';
import Text from 'src/components/Text/Text';
import { parseISO, parse, isValid, format as formatDateFns } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import 'src/styles/datepicker-overrides.css';
import { styles } from './WebDatePickerInput.styles';
import useOnClickOutside from 'src/hooks/useOnClickOutside';

import calendarIcon from 'src/assets/calendar-3x.png';

type WebDatePickerInputProps = {
  label: string;
  valueISO: string | null; // "yyyy-MM-dd" or null
  onChangeISO: (iso: string | null) => void;
  disabled?: boolean;
  testID?: string;
  isError?: boolean;
  errorText?: string;
};

// Helpers
const toISO = (d: Date | null) => (d ? formatDateFns(d, 'yyyy-MM-dd') : null);
const isoToDate = (iso: string | null) => {
  if (!iso) return null;
  const d = parseISO(iso);
  return isValid(d) ? d : null;
};
const formatMMDDYYYY = (d: Date | null) => (d ? formatDateFns(d, 'MM/dd/yyyy') : '');

const WebDatePickerInput: React.FC<WebDatePickerInputProps> = ({
  label,
  valueISO,
  onChangeISO,
  disabled,
  testID,
  isError,
  errorText = 'Invalid date',
}) => {
  const selected = useMemo(() => isoToDate(valueISO), [valueISO]);
  const [text, setText] = useState<string>(formatMMDDYYYY(selected));
  const [localError, setLocalError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<View>(null);

  // Parent error takes precedence over local parsing error
  const propError = isError ? errorText : null;
  const displayError = propError ?? localError;
  const hasError = Boolean(displayError);

  // Close datepicker when clicking outside
  useOnClickOutside(containerRef, () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  useEffect(() => {
    // Sync text buffer with parent valueISO changes
    setText(formatMMDDYYYY(isoToDate(valueISO)));
    // Only clear local error if parent isn't currently showing an error
    if (!propError) setLocalError(null);
  }, [valueISO, propError]);

  const parseTextAndEmit = (input: string) => {
    const trimmed = input.trim();
    if (!trimmed) {
      setLocalError(null);
      if (valueISO !== null) onChangeISO(null);
      setText('');
      return;
    }

    const parsed = parse(trimmed, 'MM/dd/yyyy', new Date());
    if (isValid(parsed)) {
      const nextIso = toISO(parsed);
      setText(formatMMDDYYYY(parsed));
      if (!propError) setLocalError(null);
      if (nextIso !== valueISO) onChangeISO(nextIso);
    } else {
      setLocalError('Invalid date. Use MM/DD/YYYY.');
    }
  };

  return (
    <View ref={containerRef} style={styles.container} testID={testID}>
      <Text variant="title2" style={styles.label}>
        {label}
      </Text>

      <div style={styles.inputWrapper}>
        <DatePicker
          selected={selected}
          onChange={(date: Date | null) => {
            const nextIso = toISO(date);
            setText(formatMMDDYYYY(date));
            if (!propError) setLocalError(null);
            if (nextIso !== valueISO) onChangeISO(nextIso);
          }}
          dateFormat="MM/dd/yyyy"
          placeholderText="MM/DD/YYYY"
          customInput={
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onBlur={() => parseTextAndEmit(text)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  parseTextAndEmit(text);
                }
                if (e.key === 'ArrowDown') setIsOpen(true);
              }}
              placeholder="MM/DD/YYYY"
              disabled={disabled}
              className="web-dp-input"
              style={{
                height: 40,
                border: hasError ? '1px solid #E5484D' : '1px solid #C9CFD6',
                borderRadius: 8,
                padding: '0 10px',
                fontSize: 16,
                width: '100%',
                background: disabled ? '#F0F0F0' : '#FFF',
                outline: 'none',
              }}
            />
          }
          open={isOpen}
          onCalendarOpen={() => setIsOpen(true)}
          onCalendarClose={() => setIsOpen(false)}
          shouldCloseOnSelect
          preventOpenOnFocus
          /** Force top placement, no flip, with offset, and avoid clipping */
          popperPlacement="top-start"
          /** Render popper in a portal so it’s not cut by scroll containers */
          portalId="datepickers-portal"
          popperClassName="web-dp-popper"
          showPopperArrow={false}
        />

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setIsOpen((prev) => !prev)}
          style={styles.iconButton}
          disabled={disabled}>
          <Image source={calendarIcon} style={styles.IconInput} />
        </button>
      </div>

      {displayError && <Text style={styles.error}>{displayError}</Text>}
    </View>
  );
};

export default WebDatePickerInput;
